import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Handshake,
  Plus,
  AlertCircle,
  Users,
  Tag,
  UserPlus,
  Package,
  User,
  ExternalLink,
  Globe,
  Search,
  Star,
  UsersRound,
  FileText,
  AlertTriangle,
  MessagesSquare,
  Inbox,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { PanelTour } from '../components/ui/PanelTour';
import { partnershipPanelTour } from '../config/panelTours';
import { StatTile } from '../components/ui/StatTile';
import { cn } from '../utils/cn';
import { getPartnershipPerms } from '../utils/partnershipPermissions';
import { canViewModpackConfigs } from '../utils/modpackConfigPermissions';
import { partnershipsApiService } from '../services/partnershipsApi';
import { partnersApi } from '../services/partnersApi';
import type { PartnerSummary } from '../types/partners';
import { partnerPagePath } from '../utils/partnerLinks';
import { PartnerChannelBadge, PartnerPhaseBadge } from '../components/partners/PartnerPhaseBadge';
import { PartnerDiscordAdminPanel } from '../components/partners/PartnerDiscordAdminPanel';
import { PartnerUpdatesInbox } from '../components/partners/PartnerUpdatesInbox';
import { usernameCacheService } from '../services/usernameCache';
import {
  Partnership,
  PartnershipTag,
  PartnershipStats,
  PartnershipListParams,
  PartnershipModpack,
  PartnershipCreator,
  CreatorType,
  PlatformType,
  PLATFORM_TYPES,
} from '../types/partnership';
import { PartnershipStatsCards } from '../components/partnerships/PartnershipStatsCards';
import { PartnershipCard } from '../components/partnerships/PartnershipCard';
import { PartnershipStatusBadge } from '../components/partnerships/PartnershipStatusBadge';
import { PartnershipPriorityBadge } from '../components/partnerships/PartnershipPriorityBadge';
import { PartnershipFilters } from '../components/partnerships/PartnershipFilters';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreatePartnershipModal } from '../components/partnerships/CreatePartnershipModal';
import { CreateCreatorModal } from '../components/partnerships/CreateCreatorModal';
import { CreateModpackModal } from '../components/partnerships/CreateModpackModal';
import { CreatorDetailModal } from '../components/partnerships/CreatorDetailModal';
import { ModpackDetailModal } from '../components/partnerships/ModpackDetailModal';
import { TagManagementModal } from '../components/partnerships/TagManagementModal';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { formatDistanceToNow } from 'date-fns';

type ViewMode = 'all' | 'mine' | 'of_interest' | 'creators' | 'modpacks' | 'updates' | 'discord';

const VIEW_MODES: { mode: ViewMode; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { mode: 'all', label: 'Partnerships', icon: Handshake },
  { mode: 'mine', label: 'My Partnerships', icon: Users },
  { mode: 'of_interest', label: 'Prospects', icon: Star },
  { mode: 'creators', label: 'Creators', icon: User },
  { mode: 'modpacks', label: 'Modpacks', icon: Package },
  { mode: 'updates', label: 'Partner updates', icon: Inbox },
  { mode: 'discord', label: 'Partner Discord', icon: MessagesSquare },
];

/** Layout-mirroring skeleton for the card lists. */
const ListSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="space-y-3" aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="glass-card p-4 flex items-start gap-4">
        <SkeletonLoader className="w-12 h-12 rounded-lg shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <SkeletonLoader className="h-4 w-48 max-w-full" />
          <div className="flex gap-2">
            <SkeletonLoader className="h-5 w-20 rounded-full" />
            <SkeletonLoader className="h-5 w-10 rounded-full" />
          </div>
          <SkeletonLoader className="h-3 w-full max-w-md" />
        </div>
      </div>
    ))}
  </div>
);

export const PartnershipsView = () => {
  const { identity, isLoading: authLoading } = useAuth();
  const { tourNavigation } = useTourNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const setParam = (key: string, value: string | null, replace = true) => {
    setSearchParams((current) => { const next = new URLSearchParams(current); if (value) next.set(key, value); else next.delete(key); return next; }, { replace });
  };
  const navigate = useNavigate();
  useDocumentTitle('Partnerships');

  const isDev = import.meta.env.DEV;

  const perms = useMemo(() => getPartnershipPerms(identity, isDev), [identity, isDev]);
  const canCreate = perms.canEdit;
  const canAdmin = perms.canAdmin;

  // State
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [tags, setTags] = useState<PartnershipTag[]>([]);
  const [stats, setStats] = useState<PartnershipStats | null>(null);
  const [modpackMap, setModpackMap] = useState<Record<string, PartnershipModpack>>({});
  const [creatorMap, setCreatorMap] = useState<Record<string, PartnershipCreator>>({});
  // Partner hub badges. Optional: if the endpoint fails, the badges simply do not show.
  const [partnerMap, setPartnerMap] = useState<Record<string, PartnerSummary>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const viewMode = VIEW_MODES.find((v) => v.mode === searchParams.get('tab'))?.mode ?? 'all';
  const setViewMode = (mode: ViewMode) => setParam('tab', mode, false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // Filters
  const searchQuery = searchParams.get('q') ?? '';
  const setSearchQuery = (value: string) => setParam('q', value);
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [priorityMin, setPriorityMin] = useState('');
  const [priorityMax, setPriorityMax] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Of Interest view state
  const [ofInterestPartnerships, setOfInterestPartnerships] = useState<Partnership[]>([]);
  const [ofInterestLoading, setOfInterestLoading] = useState(false);
  const [ofInterestError, setOfInterestError] = useState<string | null>(null);

  // Creators view state
  const [creators, setCreators] = useState<PartnershipCreator[]>([]);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [creatorsError, setCreatorsError] = useState<string | null>(null);
  const [creatorsTotal, setCreatorsTotal] = useState(0);
  const [creatorsPage, setCreatorsPage] = useState(1);
  const creatorsSearch = searchParams.get('creator_q') ?? '';
  const setCreatorsSearch = (value: string) => setParam('creator_q', value);
  const [creatorsTypeFilter, setCreatorsTypeFilter] = useState<CreatorType | ''>('');
  const selectedCreatorId = searchParams.get('creator');
  const setSelectedCreatorId = (value: string | null) => setParam('creator', value, !value);

  // Modpacks view state
  const [modpacks, setModpacks] = useState<PartnershipModpack[]>([]);
  const [modpacksLoading, setModpacksLoading] = useState(false);
  const [modpacksError, setModpacksError] = useState<string | null>(null);
  const [modpacksTotal, setModpacksTotal] = useState(0);
  const [modpacksPage, setModpacksPage] = useState(1);
  const modpacksSearch = searchParams.get('modpack_q') ?? '';
  const setModpacksSearch = (value: string) => setParam('modpack_q', value);
  const [modpacksPlatformFilter, setModpacksPlatformFilter] = useState<PlatformType | ''>('');
  const selectedModpackId = searchParams.get('modpack');
  const setSelectedModpackId = (value: string | null) => setParam('modpack', value, !value);

  // Modals
  const [isCreatePartnershipOpen, setIsCreatePartnershipOpen] = useState(false);
  const [isCreateCreatorOpen, setIsCreateCreatorOpen] = useState(false);
  const [isCreateModpackOpen, setIsCreateModpackOpen] = useState(false);
  const [isTagManagementOpen, setIsTagManagementOpen] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 400);
  const debouncedCreatorsSearch = useDebounce(creatorsSearch, 400);
  const debouncedModpacksSearch = useDebounce(modpacksSearch, 400);

  // Load tags for filters, and modpacks/creators for display
  useEffect(() => {
    partnershipsApiService.getAllTags()
      .then(setTags)
      .catch(err => console.error('Failed to load tags:', err));

    // Fetch modpacks and creators for card display
    partnershipsApiService.getAllModpacks({ limit: 500 })
      .then((data) => {
        const modpacks = Array.isArray(data) ? data : data.results || [];
        const map: Record<string, PartnershipModpack> = {};
        modpacks.forEach(m => { map[m.id] = m; });
        setModpackMap(map);
      })
      .catch(err => console.error('Failed to load modpacks:', err));

    partnershipsApiService.getAllCreators({ limit: 500 })
      .then((data) => {
        const creators = Array.isArray(data) ? data : data.results || [];
        const map: Record<string, PartnershipCreator> = {};
        creators.forEach(c => { map[c.id] = c; });
        setCreatorMap(map);
      })
      .catch(err => console.error('Failed to load creators:', err));
  }, []);

  const loadPartnerBadges = useCallback(() => {
    partnersApi
      .list()
      .then((list) => setPartnerMap(Object.fromEntries(list.map((p) => [p.creator_id, p]))))
      .catch((err) => console.warn('Partner badges unavailable:', err));
  }, []);

  useEffect(() => {
    loadPartnerBadges();
  }, [loadPartnerBadges]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const s = await partnershipsApiService.getPartnershipStats();
      setStats(s);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Load partnerships
  const loadPartnerships = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: PartnershipListParams = {
        page,
        limit: PAGE_SIZE,
        sort_by: sortBy as PartnershipListParams['sort_by'],
        sort_order: sortOrder,
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter as PartnershipListParams['status'];
      if (tagFilter) params.tag_id = tagFilter;
      if (priorityMin) params.priority_min = Number(priorityMin);
      if (priorityMax) params.priority_max = Number(priorityMax);

      if (viewMode === 'mine' && identity) {
        params.assigned_to = identity.raw.custom.minecraft_uuid;
      }

      const response = await partnershipsApiService.getAllPartnerships(params);
      setPartnerships(response.results || []);
      setTotalCount(response.total || 0);

      // Prefetch usernames for assigned_to, created_by
      const uuids = new Set<string>();
      (response.results || []).forEach(p => {
        if (p.assigned_to) uuids.add(p.assigned_to);
        if (p.created_by) uuids.add(p.created_by);
      });
      if (uuids.size > 0) {
        usernameCacheService.prefetchUsernames(Array.from(uuids));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load partnerships');
      setPartnerships([]);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortOrder, debouncedSearch, statusFilter, tagFilter, priorityMin, priorityMax, viewMode, identity]);

  useEffect(() => {
    if (viewMode === 'all' || viewMode === 'mine') {
      loadPartnerships();
    }
  }, [loadPartnerships, viewMode]);

  const loadOfInterest = useCallback(async () => {
    setOfInterestLoading(true);
    setOfInterestError(null);
    try {
      const data = await partnershipsApiService.getOfInterestPartnerships();
      setOfInterestPartnerships(data);
      const uuids = new Set<string>();
      data.forEach(p => { if (p.assigned_to) uuids.add(p.assigned_to); if (p.created_by) uuids.add(p.created_by); });
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setOfInterestError(err instanceof Error ? err.message : 'Failed to load of-interest partnerships');
      setOfInterestPartnerships([]);
    } finally {
      setOfInterestLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'of_interest') {
      loadOfInterest();
    }
  }, [loadOfInterest, viewMode]);

  // Load creators
  const loadCreators = useCallback(async () => {
    setCreatorsLoading(true);
    setCreatorsError(null);
    try {
      if (debouncedCreatorsSearch) {
        const results = await partnershipsApiService.searchCreators(
          debouncedCreatorsSearch,
          creatorsPage,
          PAGE_SIZE
        );
        const filtered = creatorsTypeFilter
          ? results.filter(c => c.type === creatorsTypeFilter)
          : results;
        setCreators(filtered);
        setCreatorsTotal(filtered.length);
      } else {
        const response = await partnershipsApiService.getAllCreators({
          type: creatorsTypeFilter || undefined,
          page: creatorsPage,
          limit: PAGE_SIZE,
        });
        if (Array.isArray(response)) {
          setCreators(response);
          setCreatorsTotal(response.length);
        } else {
          setCreators(response.results || []);
          setCreatorsTotal(response.total || 0);
        }
      }
    } catch (err) {
      setCreatorsError(err instanceof Error ? err.message : 'Failed to load creators');
      setCreators([]);
    } finally {
      setCreatorsLoading(false);
    }
  }, [creatorsPage, debouncedCreatorsSearch, creatorsTypeFilter]);

  useEffect(() => {
    if (viewMode === 'creators') {
      loadCreators();
    }
  }, [loadCreators, viewMode]);

  // Load modpacks
  const loadModpacks = useCallback(async () => {
    setModpacksLoading(true);
    setModpacksError(null);
    try {
      if (debouncedModpacksSearch) {
        const results = await partnershipsApiService.searchModpacks(
          debouncedModpacksSearch,
          modpacksPage,
          PAGE_SIZE
        );
        const filtered = modpacksPlatformFilter
          ? results.filter(m => m.platform_type === modpacksPlatformFilter)
          : results;
        setModpacks(filtered);
        setModpacksTotal(filtered.length);
      } else {
        const response = await partnershipsApiService.getAllModpacks({
          platform_type: modpacksPlatformFilter || undefined,
          page: modpacksPage,
          limit: PAGE_SIZE,
        });
        if (Array.isArray(response)) {
          setModpacks(response);
          setModpacksTotal(response.length);
        } else {
          setModpacks(response.results || []);
          setModpacksTotal(response.total || 0);
        }
      }
    } catch (err) {
      setModpacksError(err instanceof Error ? err.message : 'Failed to load modpacks');
      setModpacks([]);
    } finally {
      setModpacksLoading(false);
    }
  }, [modpacksPage, debouncedModpacksSearch, modpacksPlatformFilter]);

  useEffect(() => {
    if (viewMode === 'modpacks') {
      loadModpacks();
    }
  }, [loadModpacks, viewMode]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, tagFilter, priorityMin, priorityMax, viewMode]);

  useEffect(() => {
    setCreatorsPage(1);
  }, [debouncedCreatorsSearch, creatorsTypeFilter]);

  useEffect(() => {
    setModpacksPage(1);
  }, [debouncedModpacksSearch, modpacksPlatformFilter]);

  const handlePartnershipClick = (id: string) => {
    navigate(`/partnerships/${id}`);
  };

  const handleRefresh = () => {
    if (viewMode === 'creators') {
      loadCreators();
    } else if (viewMode === 'modpacks') {
      loadModpacks();
    } else if (viewMode === 'of_interest') {
      loadOfInterest();
    } else {
      loadPartnerships();
    }
    loadStats();
    loadPartnerBadges();
    partnershipsApiService.getAllTags().then(setTags).catch(console.error);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const creatorsTotalPages = Math.ceil(creatorsTotal / PAGE_SIZE);
  const modpacksTotalPages = Math.ceil(modpacksTotal / PAGE_SIZE);

  const parsePlatformLinks = (links: string | null): Record<string, string> => {
    if (!links) return {};
    try { return JSON.parse(links); } catch { return {}; }
  };

  const resultCount = (() => {
    if (viewMode === 'all' || viewMode === 'mine') return loading ? null : totalCount;
    if (viewMode === 'of_interest') return ofInterestLoading ? null : ofInterestPartnerships.length;
    if (viewMode === 'creators') return creatorsLoading ? null : creatorsTotal;
    if (viewMode === 'modpacks') return modpacksLoading ? null : modpacksTotal;
    return null;
  })();

  const renderPagination = (
    current: number,
    pages: number,
    total: number,
    onPrev: () => void,
    onNext: () => void,
  ) => (
    <div className="glass-card p-3 flex items-center justify-between gap-3">
      <p className="text-xs text-glass-400 tabular-nums">
        Showing {((current - 1) * PAGE_SIZE) + 1}–{Math.min(current * PAGE_SIZE, total)} of {total.toLocaleString()}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={current <= 1}
          className="glass-button-secondary"
        >
          Previous
        </button>
        <span className="text-xs text-glass-400 tabular-nums whitespace-nowrap">
          Page {current} of {pages}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={current >= pages}
          className="glass-button-secondary"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="PR Management"
        title="Partnerships"
        description="Manage modpack creator partnerships, track outreach, and coordinate deals."
        icon={Handshake}
        actions={
          <>
            <PanelTour tourId="partnerships" title="PR Management" steps={partnershipPanelTour(canCreate, canAdmin)} enabled={canCreate && !authLoading} {...tourNavigation} />
            {canAdmin && (
              <button
                onClick={() => setIsTagManagementOpen(true)}
                data-tour="pr-tags"
                className="glass-button-secondary"
                title="Manage tags"
              >
                <Tag className="w-4 h-4" />
                Tags
              </button>
            )}
            {canCreate && viewMode === 'creators' && (
              <>
                <button
                  onClick={() => setIsCreateCreatorOpen(true)}
                  className="glass-button"
                  title="New Creator"
                  aria-label="New Creator"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>New Creator</span>
                </button>
              </>
            )}
            {canCreate && viewMode === 'modpacks' && (
                <button
                  onClick={() => setIsCreateModpackOpen(true)}
                  className="glass-button"
                  title="New Modpack"
                  aria-label="New Modpack"
                >
                  <Package className="w-4 h-4" />
                  <span>New Modpack</span>
                </button>
            )}
            {canCreate && ['all', 'mine', 'of_interest'].includes(viewMode) && (
                <button
                  data-tour="pr-create"
                  onClick={() => setIsCreatePartnershipOpen(true)}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  New Partnership
                </button>
            )}
          </>
        }
      >
        {/* View Mode Toggle + result count */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div data-tour="pr-navigation" className="tabs flex-wrap max-w-full" role="tablist" aria-label="Partnership views">
            {VIEW_MODES.map(({ mode, label, icon: Icon }) => (
              <button
                key={mode}
                data-tour={`pr-${mode}`}
                type="button"
                role="tab"
                aria-selected={viewMode === mode}
                onClick={() => setViewMode(mode)}
                className="tab min-h-[44px]"
                data-active={viewMode === mode ? 'true' : 'false'}
                title={label}
                aria-label={label}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
          {resultCount !== null && (
            <span className="text-xs text-glass-400 tabular-nums">
              {resultCount.toLocaleString()} {resultCount === 1 ? 'result' : 'results'}
            </span>
          )}
        </div>

        {/* Filters */}
        {(viewMode === 'all' || viewMode === 'mine') && (
          <PartnershipFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            tagFilter={tagFilter}
            onTagChange={setTagFilter}
            tags={tags}
            priorityMin={priorityMin}
            onPriorityMinChange={setPriorityMin}
            priorityMax={priorityMax}
            onPriorityMaxChange={setPriorityMax}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />
        )}

        {/* Creators Filters */}
        {viewMode === 'creators' && (
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={creatorsSearch}
                data-tour="pr-creators-search"
                aria-label="Search creators"
                onChange={(e) => setCreatorsSearch(e.target.value)}
                className="glass-input w-full pl-9"
                placeholder="Search creators by name…"
              />
            </div>
            <select
              value={creatorsTypeFilter}
              onChange={(e) => setCreatorsTypeFilter(e.target.value as CreatorType | '')}
              className="glass-select md:w-44"
              aria-label="Filter by creator type"
            >
              <option value="">All Types</option>
              <option value="individual">Individual</option>
              <option value="team">Team</option>
            </select>
          </div>
        )}

        {/* Modpacks Filters */}
        {viewMode === 'modpacks' && (
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={modpacksSearch}
                data-tour="pr-modpacks-search"
                aria-label="Search modpacks"
                onChange={(e) => setModpacksSearch(e.target.value)}
                className="glass-input w-full pl-9"
                placeholder="Search modpacks by name…"
              />
            </div>
            <select
              value={modpacksPlatformFilter}
              onChange={(e) => setModpacksPlatformFilter(e.target.value as PlatformType | '')}
              className="glass-select md:w-44"
              aria-label="Filter by platform"
            >
              <option value="">All Platforms</option>
              {PLATFORM_TYPES.map((p) => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
        )}
      </PageHeader>

      {/* Stats - shown for partnership views */}
      {(viewMode === 'all' || viewMode === 'mine') && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <StatTile label="Pick up where you left off" value="My partnerships" hint="Relationships assigned to you" icon={Users} tone="accent" onClick={() => setViewMode('mine')} />
            <StatTile label="Start with a person" value="Find a creator" hint="Contacts, modpacks and partnership history" icon={User} onClick={() => setViewMode('creators')} />
            <StatTile label="Start with a pack" value="Find a modpack" hint="Creators and connected partnerships" icon={Package} onClick={() => setViewMode('modpacks')} />
          </div>
          <details className="glass-card p-4">
            <summary className="cursor-pointer text-sm font-medium text-glass-300 min-h-[28px]">Partnership overview</summary>
            <div className="pt-4"><PartnershipStatsCards stats={stats} /></div>
          </details>
        </div>
      )}

      {/* ── Partnership List (all / mine) ──────────────────────────────── */}
      {(viewMode === 'all' || viewMode === 'mine') && (
        <>
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{error}</span>
              <button type="button" onClick={loadPartnerships} className="glass-button-secondary btn-xs">Retry</button>
            </div>
          )}

          {loading && partnerships.length === 0 && <ListSkeleton />}

          {!loading && partnerships.length === 0 ? (
            <EmptyState
              icon={debouncedSearch || statusFilter || tagFilter ? Search : Handshake}
              title={
                debouncedSearch || statusFilter || tagFilter
                  ? 'No partnerships match your filters'
                  : viewMode === 'mine'
                  ? 'No partnerships assigned to you'
                  : 'No partnerships found'
              }
              description={
                canCreate && !debouncedSearch && !statusFilter
                  ? 'Click "New Partnership" to create the first one.'
                  : undefined
              }
              action={
                canCreate && !debouncedSearch && !statusFilter ? (
                  <button onClick={() => setIsCreatePartnershipOpen(true)} className="glass-button">
                    <Plus className="w-4 h-4" />
                    New Partnership
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              {(() => {
                const grouped = new Map<string, { creator?: PartnershipCreator; partnerships: Partnership[] }>();
                const ungrouped: Partnership[] = [];

                for (const partnership of partnerships) {
                  const cid = partnership.creator_id;
                  if (cid) {
                    if (!grouped.has(cid)) {
                      grouped.set(cid, {
                        creator: creatorMap[cid],
                        partnerships: [],
                      });
                    }
                    grouped.get(cid)!.partnerships.push(partnership);
                  } else {
                    ungrouped.push(partnership);
                  }
                }

                const sortedGroups = Array.from(grouped.entries()).sort(([, a], [, b]) => {
                  const nameA = a.creator?.name || '';
                  const nameB = b.creator?.name || '';
                  return nameA.localeCompare(nameB);
                });

                return (
                  <div className="space-y-3">
                    {sortedGroups.map(([creatorId, group]) => (
                      <div key={creatorId} className="glass-card overflow-hidden">
                        <div
                          className="flex items-center justify-between gap-3 px-4 py-3 border-b border-glass-200/10 cursor-pointer hover:bg-glass-200/5 transition-colors"
                          onClick={() => group.creator && setSelectedCreatorId(group.creator.id)}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="icon-tile icon-tile-accent">
                              {group.creator?.type === 'team' ? (
                                <Users className="w-4 h-4" />
                              ) : (
                                <User className="w-4 h-4" />
                              )}
                            </span>
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-white truncate">
                                {group.creator?.name || creatorId.slice(0, 8) + '...'}
                              </h3>
                              <p className="text-xs text-glass-500 tabular-nums">
                                {group.partnerships.length} partnership{group.partnerships.length !== 1 ? 's' : ''}
                                {group.creator?.type && <span className="ml-1">· {group.creator.type}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                            {(() => {
                              const partner = partnerMap[creatorId];
                              if (!partner) return null;
                              const team = partner.members.developer + partner.members.contributor;
                              return (
                                <>
                                  {partner.managed && <PartnerPhaseBadge phase={partner.phase} className="hidden sm:inline-flex" />}
                                  {partner.managed && (partner.channel.error
                                    ? <span className="badge badge-danger badge-dot" title={partner.channel.error}>Discord error</span>
                                    : <PartnerChannelBadge state={partner.channel.state} className="hidden sm:inline-flex" />)}
                                  {partner.members.issues > 0 && (
                                    <span className="badge badge-warning" title="Team members whose Discord access needs a look">
                                      <AlertTriangle className="w-3 h-3" />
                                      {partner.members.issues}
                                    </span>
                                  )}
                                  {partner.open_updates > 0 && (
                                    <Link
                                      to={partnerPagePath(creatorId, 'updates')}
                                      onClick={(e) => e.stopPropagation()}
                                      className="badge badge-info hover:brightness-125"
                                      title="Update reports waiting on staff"
                                    >
                                      <FileText className="w-3 h-3" />
                                      {partner.open_updates} open
                                    </Link>
                                  )}
                                  {team > 0 && (
                                    <span className="badge badge-neutral hidden md:inline-flex" title={`${partner.members.developer} Partnered Developers, ${partner.members.contributor} Contributors`}>
                                      {team} on team
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                            {(() => {
                              const statuses = group.partnerships.map(p => p.status);
                              const active = statuses.filter(s => s === 'active').length;
                              if (active > 0) return (
                                <span className="badge badge-success badge-dot">{active} active</span>
                              );
                              return null;
                            })()}
                            <Link
                              to={partnerPagePath(creatorId)}
                              onClick={(e) => e.stopPropagation()}
                              className="btn-ghost btn-xs"
                              data-tour="pr-partner-page"
                              title="Team, Discord channel, update reports and server packs"
                            >
                              <UsersRound className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Partner page</span>
                            </Link>
                          </div>
                        </div>
                        <div className="divide-y divide-glass-200/10">
                          {group.partnerships.map((partnership) => {
                            const modpack = partnership.modpack_id ? modpackMap[partnership.modpack_id] : undefined;
                            return (
                              <div
                                key={partnership.id}
                                onClick={() => handlePartnershipClick(partnership.id)}
                                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-glass-200/5 transition-colors"
                              >
                                {modpack?.logo_url && (
                                  <img
                                    src={modpack.logo_url}
                                    alt=""
                                    className="w-8 h-8 rounded-md object-cover bg-dark-900/50 flex-shrink-0 ring-1 ring-glass-200/10"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm text-white font-medium truncate">
                                      {modpack?.name || partnership.summary || 'Creator-level partnership'}
                                    </span>
                                    {!modpack && (
                                      <span className="chip flex-shrink-0">No modpack</span>
                                    )}
                                  </div>
                                  {partnership.summary && modpack && (
                                    <p className="text-xs text-glass-400 truncate">{partnership.summary}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <PartnershipStatusBadge status={partnership.status} />
                                  <PartnershipPriorityBadge priority={partnership.priority} />
                                  {partnership.assigned_to && (
                                    <div className="hidden sm:block">
                                      <PlayerProfile uuid={partnership.assigned_to} inline showUuid={false} showAvatar={true} />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {ungrouped.map((partnership) => {
                      const modpack = partnership.modpack_id ? modpackMap[partnership.modpack_id] : undefined;
                      const creator = modpack ? creatorMap[modpack.creator_id] : undefined;
                      return (
                        <PartnershipCard
                          key={partnership.id}
                          partnership={partnership}
                          modpack={modpack}
                          creator={creator}
                          tags={tags}
                          onClick={() => handlePartnershipClick(partnership.id)}
                        />
                      );
                    })}
                  </div>
                );
              })()}

              {totalPages > 1 && renderPagination(
                page,
                totalPages,
                totalCount,
                () => setPage(p => Math.max(1, p - 1)),
                () => setPage(p => Math.min(totalPages, p + 1)),
              )}
            </>
          )}
        </>
      )}

      {/* ── Of Interest ───────────────────────────────────────────────── */}
      {viewMode === 'of_interest' && (
        <>
          {ofInterestError && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{ofInterestError}</span>
              <button type="button" onClick={loadOfInterest} className="glass-button-secondary btn-xs">Retry</button>
            </div>
          )}

          {ofInterestLoading && ofInterestPartnerships.length === 0 && <ListSkeleton rows={3} />}

          {!ofInterestLoading && ofInterestPartnerships.length === 0 ? (
            <EmptyState
              icon={Star}
              tone="accent"
              title="No partnerships of interest"
              description='Partnerships marked as "Of Interest" by leadership will appear here for the PR team to investigate.'
            />
          ) : (
            <div className="space-y-3">
              <div className="alert bg-purple-500/10 border-purple-500/30 text-glass-300" role="note">
                <Star className="text-purple-300" />
                <p className="text-sm">
                  <span className="font-medium text-purple-300">Of Interest</span> — These are creators and packs that leadership wants the PR team to look into. Review each one and move to "Prospecting" when you start researching.
                </p>
              </div>
              {ofInterestPartnerships.map((partnership) => {
                const modpack = partnership.modpack_id ? modpackMap[partnership.modpack_id] : undefined;
                const creator = partnership.creator_id
                  ? creatorMap[partnership.creator_id]
                  : modpack ? creatorMap[modpack.creator_id] : undefined;
                return (
                  <PartnershipCard
                    key={partnership.id}
                    partnership={partnership}
                    modpack={modpack}
                    creator={creator}
                    tags={tags}
                    onClick={() => handlePartnershipClick(partnership.id)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Creators List ──────────────────────────────────────────────── */}
      {viewMode === 'creators' && (
        <>
          {creatorsError && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{creatorsError}</span>
              <button type="button" onClick={loadCreators} className="glass-button-secondary btn-xs">Retry</button>
            </div>
          )}

          {creatorsLoading && creators.length === 0 && <ListSkeleton />}

          {!creatorsLoading && creators.length === 0 ? (
            <EmptyState
              icon={debouncedCreatorsSearch || creatorsTypeFilter ? Search : User}
              title={
                debouncedCreatorsSearch || creatorsTypeFilter
                  ? 'No creators match your filters'
                  : 'No creators found'
              }
              description={canCreate ? 'Click "New Creator" to add one.' : undefined}
              action={
                canCreate ? (
                  <button onClick={() => setIsCreateCreatorOpen(true)} className="glass-button">
                    <UserPlus className="w-4 h-4" />
                    New Creator
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="space-y-3">
                {creators.map((creator) => {
                  const links = parsePlatformLinks(creator.platform_links);
                  const linkEntries = Object.entries(links);
                  return (
                    <div
                      key={creator.id}
                      onClick={() => setSelectedCreatorId(creator.id)}
                      className={cn('glass-card glass-card-interactive p-4 group')}
                    >
                      <div className="flex items-start gap-4">
                        <span className="icon-tile icon-tile-neutral w-10 h-10">
                          {creator.type === 'team' ? (
                            <Users className="w-4 h-4" />
                          ) : (
                            <User className="w-4 h-4" />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 min-w-0">
                            <h3 className="text-sm font-semibold text-white truncate">{creator.name}</h3>
                            <span className={cn(
                              'badge capitalize',
                              creator.type === 'team' ? 'badge-purple' : 'badge-info'
                            )}>
                              {creator.type}
                            </span>
                          </div>
                          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-glass-500">
                            {creator.contact_info && (
                              <span className="truncate max-w-[200px]">{creator.contact_info}</span>
                            )}
                            {creator.website && (
                              <span className="inline-flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {creator.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                              </span>
                            )}
                            {linkEntries.length > 0 && (
                              <span className="inline-flex items-center gap-1 tabular-nums">
                                <ExternalLink className="w-3 h-3" />
                                {linkEntries.length} platform{linkEntries.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            <span className="tabular-nums">
                              Created {formatDistanceToNow(new Date(creator.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          {creator.notes && (
                            <p className="text-xs text-glass-400 mt-1 line-clamp-1">{creator.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {creatorsTotalPages > 1 && renderPagination(
                creatorsPage,
                creatorsTotalPages,
                creatorsTotal,
                () => setCreatorsPage(p => Math.max(1, p - 1)),
                () => setCreatorsPage(p => Math.min(creatorsTotalPages, p + 1)),
              )}
            </>
          )}
        </>
      )}

      {/* ── Modpacks List ──────────────────────────────────────────────── */}
      {viewMode === 'modpacks' && (
        <>
          {modpacksError && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{modpacksError}</span>
              <button type="button" onClick={loadModpacks} className="glass-button-secondary btn-xs">Retry</button>
            </div>
          )}

          {modpacksLoading && modpacks.length === 0 && <ListSkeleton />}

          {!modpacksLoading && modpacks.length === 0 ? (
            <EmptyState
              icon={debouncedModpacksSearch || modpacksPlatformFilter ? Search : Package}
              title={
                debouncedModpacksSearch || modpacksPlatformFilter
                  ? 'No modpacks match your filters'
                  : 'No modpacks found'
              }
              description={canCreate ? 'Click "New Modpack" to add one.' : undefined}
              action={
                canCreate ? (
                  <button onClick={() => setIsCreateModpackOpen(true)} className="glass-button">
                    <Package className="w-4 h-4" />
                    New Modpack
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="space-y-3">
                {modpacks.map((modpack) => {
                  const creator = creatorMap[modpack.creator_id];
                  return (
                    <div
                      key={modpack.id}
                      onClick={() => setSelectedModpackId(modpack.id)}
                      className={cn('glass-card glass-card-interactive p-4 group')}
                    >
                      <div className="flex items-start gap-4">
                        {modpack.logo_url ? (
                          <img
                            src={modpack.logo_url}
                            alt={modpack.name}
                            className="w-12 h-12 rounded-lg object-cover bg-dark-900/50 flex-shrink-0 ring-1 ring-glass-200/10"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <span className="icon-tile icon-tile-purple w-12 h-12">
                            <Package className="w-5 h-5" />
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 min-w-0">
                            <h3 className="text-sm font-semibold text-white truncate">{modpack.name}</h3>
                            {modpack.platform_type && (
                              <span className="chip capitalize">
                                {modpack.platform_type}
                              </span>
                            )}
                          </div>
                          {creator && (
                            <p className="text-xs text-glass-400 mb-1 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {creator.name}
                              <span className="text-glass-500">({creator.type})</span>
                            </p>
                          )}
                          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-glass-500 tabular-nums">
                            {modpack.player_count_estimate && (
                              <span>{modpack.player_count_estimate.toLocaleString()} est. players</span>
                            )}
                            {modpack.platform_url && (
                              <span className="inline-flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" />
                                {modpack.platform_type || 'Platform'}
                              </span>
                            )}
                            <span>
                              Created {formatDistanceToNow(new Date(modpack.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          {modpack.description && (
                            <p className="text-xs text-glass-400 mt-1 line-clamp-1">{modpack.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {modpacksTotalPages > 1 && renderPagination(
                modpacksPage,
                modpacksTotalPages,
                modpacksTotal,
                () => setModpacksPage(p => Math.max(1, p - 1)),
                () => setModpacksPage(p => Math.min(modpacksTotalPages, p + 1)),
              )}
            </>
          )}
        </>
      )}

      {/* ── Partner Discord ───────────────────────────────────────────── */}
      {viewMode === 'updates' && <PartnerUpdatesInbox canEdit={canCreate} canOpenConfigs={isDev || canViewModpackConfigs(identity)} />}
      {viewMode === 'discord' && <PartnerDiscordAdminPanel canEdit={canCreate} />}

      {/* Modals */}
      {isCreatePartnershipOpen && (
        <CreatePartnershipModal
          isOpen={isCreatePartnershipOpen}
          onClose={() => setIsCreatePartnershipOpen(false)}
          onSuccess={() => {
            setIsCreatePartnershipOpen(false);
            handleRefresh();
          }}
        />
      )}

      {isCreateCreatorOpen && (
        <CreateCreatorModal
          isOpen={isCreateCreatorOpen}
          onClose={() => setIsCreateCreatorOpen(false)}
          onSuccess={() => {
            setIsCreateCreatorOpen(false);
            handleRefresh();
          }}
        />
      )}

      {isCreateModpackOpen && (
        <CreateModpackModal
          isOpen={isCreateModpackOpen}
          onClose={() => setIsCreateModpackOpen(false)}
          onSuccess={() => {
            setIsCreateModpackOpen(false);
            handleRefresh();
          }}
        />
      )}

      {isTagManagementOpen && (
        <TagManagementModal
          isOpen={isTagManagementOpen}
          onClose={() => {
            setIsTagManagementOpen(false);
            partnershipsApiService.getAllTags().then(setTags).catch(console.error);
          }}
        />
      )}

      {/* Creator Detail Modal */}
      {selectedCreatorId && (
        <CreatorDetailModal
          creatorId={selectedCreatorId}
          isOpen={!!selectedCreatorId}
          onClose={() => setSelectedCreatorId(null)}
          onUpdate={() => {
            loadCreators();
            loadStats();
          }}
          canEdit={canCreate}
          canDelete={canAdmin}
        />
      )}

      {/* Modpack Detail Modal */}
      {selectedModpackId && (
        <ModpackDetailModal
          modpackId={selectedModpackId}
          isOpen={!!selectedModpackId}
          onClose={() => setSelectedModpackId(null)}
          onUpdate={() => {
            loadModpacks();
            loadStats();
          }}
          canEdit={canCreate}
          canDelete={canAdmin}
        />
      )}
    </div>
  );
};
